import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, AfterViewChecked, ChangeDetectorRef, ElementRef, Renderer2 } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { LoggerService } from 'src/services/logger-services/logger.service';
import { RegulationNextService } from 'src/services/regulation-next/regulation-next.service';
import { RegulationReferenceMappingService } from 'src/shared/services/regulation-reference-mapping/regulation-reference-mapping.service';
import { environment } from 'src/environments/environment';
import config from '../../../config/config.json';
import {
  extractInsIdsFromTableData,
  extractLinkableInsIdsFromText,
  isCompactLegacyInsId,
  LINKABLE_INS_ID_TOKEN_REGEX,
  mapTableDataInsIds,
} from '../../utils/ins-id.util';
import {
  MarkdownSegment,
  rehydrateFlattenedTableRows,
  tableStructureKey,
  parseAgentMarkdownSegments,
  hasAgentMarkdownSegments,
} from '../../utils/markdown-table.util';
import type { AgentFilePreviewRequest } from '../agent-file-attachment-card/agent-file-attachment-card.component';

/**
 * Shared Markdown Renderer Component
 * 
 * Renders markdown content with support for:
 * - Markdown formatting (headings, lists, code, blockquotes, etc.)
 * - LaTeX/KaTeX equations
 * - Base64 images
 * - Tables with scrolling
 * - Flag emojis
 * 
 * Used by agent and chatbot interfaces to render markdown content
 */
@Component({
  selector: 'app-shared-markdown-renderer',
  templateUrl: './markdown-renderer.component.html',
  styleUrls: ['./markdown-renderer.component.scss']
})
export class MarkdownRendererComponent implements OnInit, AfterViewChecked, OnChanges {
  private static readonly VIA_INS_ORIGINAL_ID_ATTR = 'data-via-ins-original-id';

  /**
   * Markdown content to render (can include LaTeX, tables, etc.)
   */
  @Input() content: string = '';
  
  /**
   * CSS class to apply to wrapper
   */
  @Input() wrapperClass: string = 'markdown-answer';
  
  /**
   * Enable table scrolling
   */
  @Input() enableScrolling: boolean = true;
  
  /**
   * Enable LaTeX equation width detection for scrollbars
   */
  @Input() enableLatexScrollbars: boolean = true;
  
  /**
   * Enable table class detection (wide-table for >5 columns)
   */
  @Input() enableTableClassDetection: boolean = true;
  
  /**
   * Enable link processing (open in new tabs)
   */
  @Input() enableLinkProcessing: boolean = true;
  
  /**
   * Enable text-based list formatting
   */
  @Input() enableListFormatting: boolean = true;

  /**
   * Current agent ID (used for agent-specific rendering behavior).
   */
  @Input() agentId: string = '';

  /**
   * Fusion App UI base URL used to build INS hyperlinks.
   */
  @Input() fusionAppUiUrl: string = '';

  /**
   * Indicates whether response text is currently streaming.
   * Mapping API is triggered only after streaming completes.
   */
  @Input() isStreaming: boolean = false;

  /**
   * When false, INS tokens are shown as plain text (no hyperlink / redirect).
   * Used by shared chat viewers where regulation navigation is disabled.
   */
  @Input() enableInsLinkNavigation: boolean = true;

  /** When true, pipe tables render in markdown-rich-table (Via / Regulations). */
  @Input() enableRichTables = false;

  /** When true, inline s3:// paths render as file attachment cards (RIA). */
  @Input() enableS3FileAttachments = false;

  /** When false, file attachment cards render display-only (no preview/download). */
  @Input() showFileAttachmentActions = true;

  /** S3 paths from history metadata for attachments not embedded in answer text. */
  @Input() filePathsInOrder: string[] = [];

  /** When true, RIA rich tables show keyword search that filters rows. */
  @Input() enableTableSearch = false;

  /** When true, RIA rich tables show per-column value filters. */
  @Input() enableTableColumnFilter = false;

  /** When false, rich tables hide the download action. */
  @Input() enableTableDownload = true;

  /** When false, rich tables render all rows without pagination. Default true. */
  @Input() isTablePaginationEnabled = true;

  @Output() tableDownloaded = new EventEmitter<string>();
  @Output() tableDownloadFailed = new EventEmitter<void>();
  @Output() filePreviewRequested = new EventEmitter<AgentFilePreviewRequest>();
  @Output() filePreviewFailed = new EventEmitter<string>();
  @Output() fileDownloadCompleted = new EventEmitter<string>();
  @Output() fileDownloadFailed = new EventEmitter<string>();

  segments: readonly MarkdownSegment[] = [];
  useSegmentedRichTables = false;

  // Flag to prevent infinite loops
  private isUpdatingMarkdown = false;
  // Marks when markdown content/inputs changed and DOM needs re-processing
  private shouldReprocessMarkdown = true;
  /** Avoid repeating identical linkify debug logs on every change-detection pass. */
  private lastViaInsLinkifyLogKey = '';
  /** Skips duplicate RIA table INS mapping runs for the same content + tenant + ids. */
  private lastRiaTableInsMappingKey = '';
  private isApplyingRiaTableInsMapping = false;

  constructor(
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2,
    private readonly logger: LoggerService,
    private readonly regulationNextService: RegulationNextService,
    private readonly regulationReferenceMappingService: RegulationReferenceMappingService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  /**
   * Component initialization
   */
  ngOnInit(): void {
    try {
      this.rebuildSegments();
    } catch (error: any) {
      this.logger.logInfo(`Error in ngOnInit: ${error?.message || error}`);
    }
  }



  ngOnChanges(changes: SimpleChanges): void {
    const shouldRebuildSegments =
      changes['content'] ||
      changes['agentId'] ||
      changes['fusionAppUiUrl'] ||
      changes['isStreaming'] ||
      changes['enableRichTables'] ||
      changes['enableS3FileAttachments'] ||
      changes['filePathsInOrder'] ||
      changes['enableTableSearch'] ||
      changes['enableTableColumnFilter'] ||
      changes['isTablePaginationEnabled'];

    if (!shouldRebuildSegments) {
      return;
    }

    this.shouldReprocessMarkdown = true;
    this.lastViaInsLinkifyLogKey = '';

    if (changes['content'] || changes['agentId'] || changes['isStreaming']) {
      this.lastRiaTableInsMappingKey = '';
    }

    this.rebuildSegments();
  }

  private rebuildSegments(): void {
    const normalized = rehydrateFlattenedTableRows(this.content ?? '');
    this.useSegmentedRichTables =
      this.enableRichTables &&
      hasAgentMarkdownSegments(normalized, {
        enableS3Attachments: this.enableS3FileAttachments,
        isStreaming: this.isStreaming,
        filePathsInOrder: this.filePathsInOrder,
      });
    this.segments = this.useSegmentedRichTables
      ? parseAgentMarkdownSegments(normalized, {
          enableS3Attachments: this.enableS3FileAttachments,
          isStreaming: this.isStreaming,
          filePathsInOrder: this.filePathsInOrder,
        })
      : [];

    if (!this.isStreaming) {
      this.scheduleRiaTableInsIdMapping();
    }
  }

  /**
   * RIA agent: map legacy INS IDs in structured table data to tenant-specific IDs
   * so table cells, column filters, and keyword search use the API-provided values.
   */
  private scheduleRiaTableInsIdMapping(): void {
    const isRiaAgent = (this.agentId || '').trim().toLowerCase() === 'ria_agent';
    if (!isRiaAgent || !this.shouldCallReferenceMappingApi() || !this.useSegmentedRichTables) {
      return;
    }

    const allInsIds = new Set<string>();
    for (const segment of this.segments) {
      if (segment.kind !== 'table' || segment.tableData === null) {
        continue;
      }
      extractInsIdsFromTableData(segment.tableData).forEach((insId) => allInsIds.add(insId));
    }
    if (allInsIds.size === 0) {
      return;
    }

    const insIds = Array.from(allInsIds);
    const mappingKey =
      `${this.getTenantId()}::${this.content ?? ''}::${insIds
        .sort((a, b) => a.localeCompare(b))
        .join(',')}`;
    if (mappingKey === this.lastRiaTableInsMappingKey || this.isApplyingRiaTableInsMapping) {
      return;
    }

    this.isApplyingRiaTableInsMapping = true;

    this.resolveInsIdMappings(insIds)
      .pipe(take(1))
      .subscribe({
        next: (mappings) => {
          const hasMappedChanges = insIds.some((insId) => mappings.get(insId) !== insId);
          if (hasMappedChanges) {
            this.segments = this.segments.map((segment) => {
              if (segment.kind !== 'table' || segment.tableData === null) {
                return segment;
              }
              return {
                ...segment,
                tableData: mapTableDataInsIds(segment.tableData, mappings),
              };
            });
            this.shouldReprocessMarkdown = true;
            this.changeDetectorRef.markForCheck();
          }
          this.lastRiaTableInsMappingKey = mappingKey;
          this.isApplyingRiaTableInsMapping = false;
        },
        error: (error) => {
          this.isApplyingRiaTableInsMapping = false;
          this.logger.logError(
            `[RiaTableInsMapping] Failed | ids=${insIds.join(', ')}, ` +
              `${(error as Error)?.message || error}`,
            'scheduleRiaTableInsIdMapping',
            undefined,
            true,
          );
        },
      });
  }

  /** Keeps rich-table component instance alive while the same table streams more rows. */
  trackSegment(index: number, segment: MarkdownSegment): string {
    if (segment.kind === 'markdown') {
      return `md-${index}`;
    }
    if (segment.kind === 'fileAttachment') {
      return `file-${index}-${segment.presignedUrl ?? segment.s3Path}`;
    }
    const structure =
      segment.tableData !== null
        ? tableStructureKey(segment.tableData)
        : segment.tableMarkdown.split('\n')[0]?.trim() ?? '';
    return `table-${index}-${structure ?? ''}`;
  }

  /**
   * Applies dynamic processing after view is rendered
   * Handles table classes, LaTeX scrollbars, and link processing
   */
  ngAfterViewChecked(): void {
    try {
      // Defer heavy DOM link/list processing until streaming completes (all agents).
      if (this.isStreaming) {
        return;
      }
      if (this.enableTableClassDetection) {
        this.updateTableClasses();
      }
      if (this.enableLatexScrollbars) {
        this.updateLatexScrollbars();
      }
      if (this.enableLinkProcessing || this.enableListFormatting) {
        this.updateMarkdownLinks();
      }
    } catch (error: any) {
      this.logger.logInfo(`Error in ngAfterViewChecked: ${error?.message || error}`);
    }
  }

  /**
   * Checks if content exists and is not empty
   * @returns true if content is available
   */
  hasContent(): boolean {
    try {
      return !!this.content && this.content.trim().length > 0;
    } catch (error: any) {
      this.logger.logInfo(`Error in hasContent: ${error?.message || error}`);
      return false;
    }
  }

  /**
   * Dynamically applies 'wide-table' class to tables with more than 5 columns
   * This allows for better styling and scrolling behavior
   */
  private updateTableClasses(): void {
    try {
      const tables = this.elementRef.nativeElement.querySelectorAll('.markdown-content table');
      Array.from(tables as NodeListOf<HTMLTableElement>)
        .filter((table) => !table.closest('[data-rich-table]'))
        .forEach((table: HTMLTableElement) => {
        const columnCount = table.querySelector('tr')?.querySelectorAll('th, td').length || 0;
        if (columnCount > 5) {
          this.renderer.addClass(table, 'wide-table');
        } else {
          this.renderer.removeClass(table, 'wide-table');
        }
      });
    } catch (error: any) {
      this.logger.logInfo(`Error in updateTableClasses: ${error?.message || error}`);
    }
  }

  /**
   * Dynamically applies 'wide-equation' class to LaTeX equations that exceed 80% of container width
   * This triggers scrollbar visibility for long equations
   */
  private updateLatexScrollbars(): void {
    try {
      const latexEquations = this.elementRef.nativeElement.querySelectorAll('.markdown-content .katex-display');
      latexEquations.forEach((equation: HTMLElement) => {
        const container = equation.closest('.table-scroll-wrapper');
        if (container) {
          const containerWidth = container.clientWidth;
          const equationWidth = equation.scrollWidth;
          const widthPercentage = (equationWidth / containerWidth) * 100;
          
          if (widthPercentage > 80) {
            this.renderer.addClass(equation, 'wide-equation');
          } else {
            this.renderer.removeClass(equation, 'wide-equation');
          }
        }
      });

      // Also check inline LaTeX equations
      const inlineLatexEquations = this.elementRef.nativeElement.querySelectorAll('.markdown-content .katex:not(.katex-display)');
      inlineLatexEquations.forEach((equation: HTMLElement) => {
        const container = equation.closest('.table-scroll-wrapper');
        if (container) {
          const containerWidth = container.clientWidth;
          const equationWidth = equation.scrollWidth;
          const widthPercentage = (equationWidth / containerWidth) * 100;
          
          if (widthPercentage > 80) {
            this.renderer.addClass(equation, 'wide-equation');
          } else {
            this.renderer.removeClass(equation, 'wide-equation');
          }
        }
      });
    } catch (error: any) {
      this.logger.logInfo(`Error in updateLatexScrollbars: ${error?.message || error}`);
    }
  }

  /**
   * Updates markdown links to open in new tabs and formats text-based lists
   * This is called after view is rendered to process the HTML
   */
  private updateMarkdownLinks(): void {
    // Prevent infinite loops
    if (this.isUpdatingMarkdown) {
      return;
    }
    
    this.isUpdatingMarkdown = true;
    
    try {
      const markdownElements = this.elementRef.nativeElement.querySelectorAll('.markdown-content');

      const richTableCells = this.elementRef.nativeElement.querySelectorAll(
        '[data-rich-table] .rich-table__cell-content',
      );

      if (this.shouldReprocessMarkdown) {
        markdownElements.forEach((markdownEl: HTMLElement) => {
          markdownEl.removeAttribute('data-processed');
          markdownEl.removeAttribute('data-lists-processed');
        });
        richTableCells.forEach((cell: Element) => {
          cell.removeAttribute('data-processed');
        });
      }

      markdownElements.forEach((markdownEl: HTMLElement) => {
        if (markdownEl.hasAttribute('data-processed')) {
          return;
        }
        this.processMarkdownContainer(markdownEl);
      });

      richTableCells.forEach((cell: Element) => {
        const cellEl = cell as HTMLElement;
        if (cellEl.hasAttribute('data-processed')) {
          return;
        }
        if (this.enableLinkProcessing) {
          this.linkifyViaInsReferences(cellEl, () => {
            this.applyExternalLinkTargets(cellEl);
            cellEl.setAttribute('data-processed', 'true');
          });
        } else {
          cellEl.setAttribute('data-processed', 'true');
        }
      });

      this.shouldReprocessMarkdown = false;
    } catch (error: any) {
      this.logger.logInfo(`Error in updateMarkdownLinks: ${error?.message || error}`);
    } finally {
      this.isUpdatingMarkdown = false;
    }
  }

  /**
   * Post-process a markdown container (paragraphs or rich-table cells).
   * Skips list reformatting inside rich tables to avoid breaking table layout.
   */
  private processMarkdownContainer(markdownEl: HTMLElement): void {
    const isRichTable = !!markdownEl.closest('[data-rich-table]');

    if (!isRichTable && this.enableListFormatting) {
      this.formatTextBasedLists(markdownEl);
    }

    if (this.enableLinkProcessing) {
      this.linkifyViaInsReferences(markdownEl, () => {
        this.applyExternalLinkTargets(markdownEl);
        markdownEl.setAttribute('data-processed', 'true');
      });
      return;
    }

    markdownEl.setAttribute('data-processed', 'true');
  }

  private applyExternalLinkTargets(container: HTMLElement): void {
    container.querySelectorAll('a[href]').forEach((link: Element) => {
      const href = link.getAttribute('href');
      if (!href || link.getAttribute('target') === '_blank') {
        return;
      }
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      if (href.startsWith('http://') || href.startsWith('https://')) {
        link.setAttribute('referrerpolicy', 'no-referrer');
      }
    });
  }

  /** VIA and RIA agents: resolve INS mappings (cache or API), then convert tokens into links. */
  private shouldLinkifyInsReferences(): boolean {
    const agentId = (this.agentId || '').trim().toLowerCase();
    return agentId === 'via_agent' || agentId === 'ria_agent';
  }

  private linkifyViaInsReferences(container: HTMLElement, onComplete: () => void): void {
    if (!this.shouldLinkifyInsReferences()) {
      onComplete();
      return;
    }

    if (this.isStreaming) {
      onComplete();
      return;
    }

    const baseUrl = (this.fusionAppUiUrl || (config as any)?.FusionAppUIUrl || '').trim();
    if (!baseUrl) {
      onComplete();
      return;
    }

    const uniqueInsIds = this.collectUniqueInsIds(container);
    if (uniqueInsIds.length === 0) {
      onComplete();
      return;
    }

    const hasRegulationNextAccess = this.regulationNextService.hasRegulationNextAccess();
    const mappingResolveStartedAt = performance.now();

    this.resolveInsIdMappings(uniqueInsIds)
      .pipe(take(1))
      .subscribe({
        next: (resolvedInsIds) => {
          this.applyInsLinkifyToContainer(
            container,
            resolvedInsIds,
            baseUrl,
            hasRegulationNextAccess
          );
          this.applyInsLinkifyToInlineCodeElements(
            container,
            resolvedInsIds,
            baseUrl,
            hasRegulationNextAccess
          );
          const linkifyLogKey =
            `${uniqueInsIds.join(',')}|${this.formatInsIdMapping(resolvedInsIds)}|` +
            `${this.shouldCallReferenceMappingApi()}`;
          if (linkifyLogKey !== this.lastViaInsLinkifyLogKey) {
            this.lastViaInsLinkifyLogKey = linkifyLogKey;
            this.logViaInsMappingStep(
              'Linkify complete',
              `ids=${uniqueInsIds.join(', ')}, mappings=${this.formatInsIdMapping(resolvedInsIds)}, ` +
                `mappingEnabled=${this.shouldCallReferenceMappingApi()}, ` +
                `durationMs=${this.formatDurationMs(mappingResolveStartedAt)}`
            );
          }
          onComplete();
          this.changeDetectorRef.markForCheck();
        },
        error: (error) => {
          const fallbackIds = new Map(uniqueInsIds.map((insId) => [insId, insId]));
          this.applyInsLinkifyToContainer(
            container,
            fallbackIds,
            baseUrl,
            hasRegulationNextAccess
          );
          this.logger.logError(
            `[ViaInsLinkify] Linkify failed, used original ids | ids=${uniqueInsIds.join(', ')}, ` +
              `durationMs=${this.formatDurationMs(mappingResolveStartedAt)}, ` +
              `${(error as Error)?.message || error}`,
            'linkifyViaInsReferences',
            undefined,
            true
          );
          onComplete();
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  private logViaInsMappingStep(step: string, detail: string): void {
    this.logger.logInfo(`[ViaInsLinkify] ${step} | ${detail}`, 'linkifyViaInsReferences', undefined, true);
  }

  private formatInsIdMapping(resolvedInsIds: Map<string, string>): string {
    return Array.from(resolvedInsIds.entries())
      .map(([originalId, mappedId]) => `${originalId}->${mappedId}`)
      .join(', ');
  }

  private formatDurationMs(startedAt: number): number {
    return Math.round(performance.now() - startedAt);
  }

  private collectUniqueInsIds(container: HTMLElement): string[] {
    const insIds = new Set<string>();
    this.getEligibleTextNodes(container).forEach((textNode: Text) => {
      extractLinkableInsIdsFromText(textNode.nodeValue || '').forEach((insId) => insIds.add(insId));
    });
    this.getInlineInsOnlyCodeElements(container).forEach((codeEl) => {
      const insId = (codeEl.textContent || '').trim().toUpperCase();
      if (insId) {
        insIds.add(insId);
      }
    });
    return Array.from(insIds);
  }

  private resolveInsIdMappings(insIds: string[]): Observable<Map<string, string>> {
    const identityMappings = new Map(insIds.map((insId) => [insId, insId]));
    if (!this.shouldCallReferenceMappingApi()) {
      return of(identityMappings);
    }

    const compactInsIds = insIds.filter((insId) => isCompactLegacyInsId(insId));
    if (compactInsIds.length === 0) {
      return of(identityMappings);
    }

    const tenantId = this.getTenantId();
    return forkJoin(
      compactInsIds.map((insId) =>
        this.regulationReferenceMappingService.getMappedRegulationId(insId, insId, tenantId).pipe(
          map((mappedId) => [insId, (mappedId || insId).toUpperCase()] as const)
        )
      )
    ).pipe(
      map((entries) => {
        const mappings = new Map(identityMappings);
        entries.forEach(([originalId, mappedId]) => mappings.set(originalId, mappedId));
        return mappings;
      })
    );
  }

  private applyInsLinkifyToContainer(
    container: HTMLElement,
    resolvedInsIds: Map<string, string>,
    baseUrl: string,
    hasRegulationNextAccess: boolean
  ): void {
    this.getEligibleTextNodes(container).forEach((textNode: Text) => {
      const originalText = textNode.nodeValue || '';
      const matches = Array.from(originalText.matchAll(LINKABLE_INS_ID_TOKEN_REGEX));
      if (matches.length === 0) {
        return;
      }

      const fragment = document.createDocumentFragment();
      let cursor = 0;

      matches.forEach((match) => {
        const matchIndex = typeof match.index === 'number' ? match.index : -1;
        if (matchIndex < 0) {
          return;
        }

        if (matchIndex > cursor) {
          fragment.appendChild(document.createTextNode(originalText.slice(cursor, matchIndex)));
        }

        const matchedInsId = (match[1] || '').toUpperCase();
        const displayedInsId = resolvedInsIds.get(matchedInsId) || matchedInsId;
        fragment.appendChild(
          this.buildInsLinkifyReplacement(
            matchedInsId,
            displayedInsId,
            baseUrl,
            hasRegulationNextAccess
          )
        );

        cursor = matchIndex + match[0].length;
      });

      if (cursor < originalText.length) {
        fragment.appendChild(document.createTextNode(originalText.slice(cursor)));
      }

      textNode.parentNode?.replaceChild(fragment, textNode);
    });
  }

  /** Linkify inline <code>INS123</code> tokens (markdown backticks). Skips code blocks and non-INS content. */
  private applyInsLinkifyToInlineCodeElements(
    container: HTMLElement,
    resolvedInsIds: Map<string, string>,
    baseUrl: string,
    hasRegulationNextAccess: boolean
  ): void {
    this.getInlineInsOnlyCodeElements(container).forEach((codeEl) => {
      const matchedInsId = (codeEl.textContent || '').trim().toUpperCase();
      const displayedInsId = resolvedInsIds.get(matchedInsId) || matchedInsId;
      const replacement = this.buildInsLinkifyReplacement(
        matchedInsId,
        displayedInsId,
        baseUrl,
        hasRegulationNextAccess
      );
      codeEl.parentNode?.replaceChild(replacement, codeEl);
    });
  }

  private buildInsLinkifyReplacement(
    matchedInsId: string,
    displayedInsId: string,
    baseUrl: string,
    hasRegulationNextAccess: boolean
  ): Node {
    const href = this.enableInsLinkNavigation
      ? hasRegulationNextAccess
        ? this.regulationNextService.buildRegulationNextUrl(baseUrl, displayedInsId)
        : this.regulationNextService.buildLegacyRegulationUrl(baseUrl, displayedInsId)
      : '';

    if (!href) {
      return document.createTextNode(displayedInsId);
    }

    const anchor = this.renderer.createElement('a') as HTMLAnchorElement;
    this.renderer.setAttribute(anchor, 'href', href);
    this.renderer.setAttribute(anchor, 'target', '_blank');
    this.renderer.setAttribute(anchor, 'rel', 'noopener noreferrer');
    this.renderer.setAttribute(anchor, 'referrerpolicy', 'no-referrer');
    this.renderer.setAttribute(
      anchor,
      MarkdownRendererComponent.VIA_INS_ORIGINAL_ID_ATTR,
      matchedInsId
    );
    this.renderer.appendChild(anchor, this.renderer.createText(displayedInsId));
    return anchor;
  }

  private getInlineInsOnlyCodeElements(root: HTMLElement): HTMLElement[] {
    const result: HTMLElement[] = [];
    root.querySelectorAll('code').forEach((code) => {
      if (code.closest('pre')) {
        return;
      }
      const text = (code.textContent || '').trim();
      const ids = extractLinkableInsIdsFromText(text);
      if (ids.length === 1 && ids[0] === text.toUpperCase()) {
        result.push(code as HTMLElement);
      }
    });
    return result;
  }

  /**
   * Mapping API should run only for valid tenants after streaming is complete.
   */
  private shouldCallReferenceMappingApi(): boolean {
    const tenantId = this.getTenantId();
    return !this.isStreaming && !!tenantId && tenantId !== '0';
  }

  private getTenantId(): string {
    return String(environment.TenantId || '').trim();
  }

  /**
   * Collect text nodes that may contain INS IDs, excluding already-linked/code areas.
   */
  private getEligibleTextNodes(root: HTMLElement): Text[] {
    const result: Text[] = [];

    const walk = (node: Node): void => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue || '';
        if (extractLinkableInsIdsFromText(text).length === 0) {
          return;
        }
        const parentElement = node.parentElement;
        if (parentElement?.closest('a, code, pre, script, style')) {
          return;
        }
        result.push(node as Text);
        return;
      }

      node.childNodes.forEach((child) => walk(child));
    };

    walk(root);
    return result;
  }

  /**
   * Converts text-based bullet points to proper HTML structure
   * @param container - The HTML element containing the markdown content
   */
  private formatTextBasedLists(container: HTMLElement): void {
    try {
      if (container.hasAttribute('data-lists-processed')) {
        return;
      }
      
      this.formatParagraphLists(container);
      this.mergeAdjacentLists(container);
      
      container.setAttribute('data-lists-processed', 'true');
    } catch (error: any) {
      this.logger.logInfo(`Error in formatTextBasedLists: ${error?.message || error}`);
    }
  }

  /**
   * Handles text-based bullet points in paragraphs
   * @param container - The HTML element containing paragraphs
   */
  private formatParagraphLists(container: HTMLElement): void {
    const paragraphs = container.querySelectorAll('p');
    
    paragraphs.forEach((p: HTMLElement) => {
      const text = p.innerHTML;
      
      // Check if paragraph contains bullet points
      if (text.includes('•') || text.includes('\n•')) {
        // Split the text by lines and process
        const lines = text.split('\n');
        let htmlContent = '';
        let inMainList = false;
        
        lines.forEach((line) => {
          const trimmedLine = line.trim();
          
          if (!trimmedLine) {
            return;
          }
          
          if (trimmedLine.startsWith('•')) {
            // Start main list if not already started
            if (!inMainList) {
              htmlContent += '<ul class="formatted-list">';
              inMainList = true;
            }
            
            const content = trimmedLine.substring(1).trim();
            htmlContent += `<li>${content}</li>`;
          }
          else if (trimmedLine) {
            // Regular text - close lists
            if (inMainList) {
              htmlContent += '</ul>';
              inMainList = false;
            }
            htmlContent += `<p>${trimmedLine}</p>`;
          }
        });
        
        // Close any remaining open lists
        if (inMainList) {
          htmlContent += '</ul>';
        }
        
        // Replace the paragraph content if we found list items
        if (htmlContent && htmlContent.includes('<ul>')) {
          p.outerHTML = htmlContent;
        }
      }
    });
  }

  /**
   * Merges P elements containing bullets ending with ":" with following UL elements
   * @param container - The HTML element containing the lists
   */
  private mergeAdjacentLists(container: HTMLElement): void {
    // Get direct children only, not all nested elements
    const directChildren = Array.from(container.children) as HTMLElement[];
    
    for (let i = 0; i < directChildren.length - 1; i++) {
      const currentElement = directChildren[i];
      const nextElement = directChildren[i + 1];
      
      // Check if current element is a P with bullets and text ending in ":"
      if (currentElement.tagName === 'P' && nextElement.tagName === 'UL') {
        const pText = currentElement.textContent?.trim() || '';
        const pHtml = currentElement.innerHTML;
        
        // Check if paragraph contains bullets and ends with ":"
        if (pHtml.includes('•') && pText.endsWith(':')) {
          // Convert the paragraph bullets to a proper UL structure
          const lines = pHtml.split('\n');
          let newUlHtml = '<ul class="formatted-list">';
          
          lines.forEach((line) => {
            const trimmedLine = line.trim();
            if (trimmedLine.startsWith('•')) {
              const content = trimmedLine.substring(1).trim();
              newUlHtml += `<li>${content}</li>`;
            } else if (trimmedLine && !trimmedLine.startsWith('•')) {
              // Handle non-bullet lines (like headers with emojis)
              newUlHtml += `</ul><p>${trimmedLine}</p><ul class="formatted-list">`;
            }
          });
          
          newUlHtml += '</ul>';
          
          // Add class to the following UL
          nextElement.classList.add('formatted-sublist');
          
          // Create the new structure
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = newUlHtml;
          const newUl = tempDiv.querySelector('ul') as HTMLElement;
          
          if (newUl) {
            // Find the last li in our new UL (should be the one ending with ":")
            const lastLi = newUl.querySelector('li:last-child');
            if (lastLi && lastLi.textContent?.trim().endsWith(':')) {
              // Move the next UL inside this li
              lastLi.appendChild(nextElement);
            }
            
            // Replace the original P element with our new structure
            currentElement.replaceWith(newUl);
            
            break; // Process one at a time to avoid issues with changing DOM
          }
        }
      }
    }
  }
}

